import { Component, Input, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { LangSrcService } from '../../../../services/lang-src/lang-src.service';
import { LangTgtService } from '../../../../services/lang-tgt/lang-tgt.service';
import { PlatformService } from '../../../../services/platform/platform.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertComponent } from '../../../alert/alert.component';
import { LevelLockedModalComponent } from '../../../level-locked-modal/level-locked-modal.component';
import { DeviceDetectorService } from 'ngx-device-detector';

interface NodePos { x: number; y: number; }
interface DecorPlacement {
  asset: number;            // 1-based PNG filename
  section: number;          // 1-based sorted node index
  dir: { dx: number; dy: number };
  sizeFactor?: number;
  rotation?: number;        // degrees, clockwise, default 0
}
interface LangDecorConfig {
  desktop: DecorPlacement[];
  mobile?: DecorPlacement[];  // falls back to desktop if absent
}

@Component({
  selector: 'app-course-map',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './course-map.component.html',
  styleUrls: ['./course-map.component.scss']
})
export class CourseMapComponent implements OnInit, AfterViewInit {

  @ViewChild('svgRef') svgRef: ElementRef;

  @Input() sections: any[] = [];
  @Input() current = 0;
  @Input() trophies: any;
  @Input() mode: 'sections' | 'levels' | 'exam' = 'sections';

  // Layout constants — computed in ngOnInit based on device
  SVG_W = 800;
  SVG_H = 900;
  R = 32;
  PAD_X = 80;   // must be > max(ROW_WAVE_AMP) so U-turn semicircle stays in SVG
  PAD_TOP = 90;
  ROW_GAP = 200; // vertical baseline-to-baseline between rows

  // Variable row sizes — minimum 3 nodes (2-node rows are always flat since sin(0)=sin(π)=0)
  private readonly ROW_SIZES   = [4, 3, 5, 3, 4, 3, 5, 3];
  // Per-row arc amplitude (px).
  // Constraint for dangerous pairs (dir=+1 then dir=-1): amp_r + amp_{r+1} < ROW_GAP - 2R - 40 (comfort margin).
  // ROW_GAP=200: limit = 200-64-40 = 96. Pairs: (1,2)=40+52=92, (3,4)=32+48=80, (5,6)=44+50=94. All < 96 ✓
  private readonly ROW_WAVE_AMP = [55, 40, 52, 32, 50, 44, 55, 28];
  // Alternating directions: even rows arc down (-1), odd rows arc up (+1).
  // Safe pairs (-1,+1) always safe (rows arc away from each other).
  private readonly ROW_WAVE_DIR = [-1,  1, -1,  1, -1,  1, -1,  1];

  // Scale factor for wave amplitude — reduced on mobile to satisfy tighter constraint
  private waveScale = 1.0;

  // Set to true when mobile layout constants are active
  private isMobileLayout = false;

  // X jitter on non-edge nodes only
  private readonly X_JITTER_MID = [0, 18, -14, 20, -18, 12, -20, 16, -8, 18, -14, 8, 16, -10, 18, -6];
  private readonly Y_JITTER     = [-6, 8, -4, 12, -8, 4, -12, 7, -10, 5, 3, -7, 10, -5, 8, -12];

  // Precomputed per-layout
  rowStarts: number[] = [];
  rowSizes:  number[] = [];

  nodePositions: NodePos[] = [];
  trackPath = '';
  donePath = '';

  decorConfigs: Record<string, LangDecorConfig> = {};

  constructor(
    private router: Router,
    private http: HttpClient,
    public langSrcSrv: LangSrcService,
    public langTgtSrv: LangTgtService,
    public platformSrv: PlatformService,
    private modal: NgbModal,
    private deviceSrv: DeviceDetectorService,
  ) {}

  ngOnInit(): void {
    this.http.get<Record<string, LangDecorConfig>>('assets/images/maps/decor-config.json')
      .toPromise().then(cfg => { if (cfg) this.decorConfigs = cfg; });

    if (this.deviceSrv.isMobile()) {
      this.R       = 26;
      this.PAD_X   = 55;
      this.PAD_TOP = 70;
      this.ROW_GAP = 160;
      this.waveScale = 0.45; // mobile: (44+55)*0.45=44.5 < 160-52-40=68 ✓
      this.SVG_W   = 340;
      this.isMobileLayout = true;
    }

    // On mobile, use 3-node rows only: spacing = (340-2×55)/2 = 115px, gap = 63px
    // (4-node rows give only 24px gap; 5-node rows 5.5px — both too cramped)
    const rowSizesConfig = this.isMobileLayout
      ? [3, 3, 3, 3, 3, 3, 3, 3]
      : this.ROW_SIZES;

    // Build per-row start/size tables
    const n = this.sections.length || 1;
    this.rowStarts = [];
    this.rowSizes  = [];
    let idx = 0, row = 0;
    while (idx < n) {
      const size = Math.min(rowSizesConfig[row % rowSizesConfig.length], n - idx);
      this.rowStarts.push(idx);
      this.rowSizes.push(size);
      idx += size;
      row++;
    }

    const numRows = this.rowStarts.length;
    const maxWave = Math.max(...this.ROW_WAVE_AMP) * this.waveScale;
    const Ru = this.ROW_GAP / 2;
    const topPad = this.R + Math.max(maxWave, Ru) + 52;
    this.SVG_H = topPad + (numRows - 1) * this.ROW_GAP + this.PAD_TOP;
    this.computeLayout();
  }

  ngAfterViewInit(): void {
    this.scrollToCurrent();
  }

  get sorted(): any[] {
    const items = [...this.sections];
    // Free-mode levels have position: null — preserve API order (already sequential)
    if (items.every(s => s.position === null)) return items;
    return items.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }

  // Returns the row index for a given node index (binary search on rowStarts)
  private getRow(nodeIndex: number): number {
    let lo = 0, hi = this.rowStarts.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (this.rowStarts[mid] <= nodeIndex) lo = mid; else hi = mid - 1;
    }
    return lo;
  }

  // ── Data-shape helpers (sections have unlocked/acquired; API levels may use locked/bestScore) ──

  isItemUnlocked(item: any): boolean {
    if (item.unlocked !== undefined) return item.unlocked;
    return !item.locked;
  }

  isItemAcquired(item: any): boolean {
    if (item.acquired !== undefined) return item.acquired;
    return (item.bestScore ?? item.score ?? 0) >= 100;
  }

  itemScore(item: any): number {
    return Math.max(0, Math.min(100, item.bestScore ?? item.score ?? 0));
  }

  progressDash(item: any): string {
    const r = this.R + 5;
    const circ = 2 * Math.PI * r;
    return `${(this.itemScore(item) / 100) * circ} ${circ}`;
  }

  progressRotate(pos: NodePos): string {
    return `rotate(-90 ${pos.x} ${pos.y})`;
  }

  computeLayout(): void {
    const sections = this.sorted;
    this.nodePositions = sections.map((_, i) => this.nodePos(i, sections.length));
    this.trackPath = this.buildPath(this.nodePositions);

    let lastActive = -1;
    sections.forEach((s, i) => {
      if (this.isItemAcquired(s) || (s.position !== null && s.position === this.current)) lastActive = i;
    });
    this.donePath = lastActive >= 0
      ? this.buildPath(this.nodePositions, lastActive + 1)
      : '';
  }

  nodePos(index: number, _total: number): NodePos {
    const row     = this.getRow(index);
    const rowStart = this.rowStarts[row];
    const rowSize  = this.rowSizes[row];
    const col      = index - rowStart;
    const reversed = row % 2 === 1;

    const usableW    = this.SVG_W - 2 * this.PAD_X;
    const colSpacing = rowSize > 1 ? usableW / (rowSize - 1) : 0;
    const baseX      = reversed
      ? this.SVG_W - this.PAD_X - col * colSpacing
      : this.PAD_X + col * colSpacing;

    // No Y jitter — it breaks the crossing constraint (jitter adds to effective amplitude).
    // No X jitter on edge nodes — keeps U-turn geometry clean.
    const isEdge = col === 0 || col === rowSize - 1;
    const xOff   = isEdge ? 0 : this.X_JITTER_MID[index % this.X_JITTER_MID.length];
    const yOff   = 0;

    // Per-row arc: amplitude and direction both vary
    const forwardCol = reversed ? (rowSize - 1 - col) : col;
    const t          = rowSize > 1 ? forwardCol / (rowSize - 1) : 0.5;
    const amp = this.ROW_WAVE_AMP[row % this.ROW_WAVE_AMP.length] * this.waveScale;
    const dir = this.ROW_WAVE_DIR[row % this.ROW_WAVE_DIR.length];
    const waveY = -dir * Math.sin(t * Math.PI) * amp;

    return {
      x: baseX + xOff,
      y: this.SVG_H - this.PAD_TOP - row * this.ROW_GAP + waveY + yOff
    };
  }

  buildPath(positions: NodePos[], drawCount = positions.length): string {
    if (!positions.length) return '';
    const n     = positions.length;
    const limit = Math.min(drawCount, n);
    let d = `M ${positions[0].x} ${positions[0].y}`;
    const k = 0.5523; // Bézier circle-approximation magic number

    for (let i = 1; i < limit; i++) {
      const prevRow = this.getRow(i - 1);
      const currRow = this.getRow(i);

      if (prevRow === currRow) {
        // Within a row: Catmull-Rom → cubic Bézier, neighbours clamped to row bounds
        const rowStart = this.rowStarts[prevRow];
        const rowEnd   = rowStart + this.rowSizes[prevRow] - 1;
        const i0 = Math.max(i - 2, rowStart);
        const i3 = Math.min(i + 1, Math.min(rowEnd, n - 1));
        const p0 = positions[i0], p1 = positions[i - 1];
        const p2 = positions[i],  p3 = positions[i3];
        const cp1x = p1.x + (p2.x - p0.x) / 5;
        const cp1y = p1.y + (p2.y - p0.y) / 5;
        const cp2x = p2.x - (p3.x - p1.x) / 5;
        const cp2y = p2.y - (p3.y - p1.y) / 5;
        d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;

      } else {
        // Row-to-row: Bézier semicircle (two quarter-circle segments)
        const p1 = positions[i - 1];
        const p2 = positions[i];
        const rightSide = p1.x > this.SVG_W / 2;
        const Ru = rightSide
          ? Math.min((p1.y - p2.y) / 2, this.SVG_W - p1.x - 8)
          : Math.min((p1.y - p2.y) / 2, p1.x - 8);
        const cy  = (p1.y + p2.y) / 2;
        const dir = rightSide ? 1 : -1;
        const cx  = p1.x;
        d += ` C ${cx + dir*k*Ru},${p1.y} ${cx + dir*Ru},${cy + k*Ru} ${cx + dir*Ru},${cy}`;
        d += ` C ${cx + dir*Ru},${cy - k*Ru} ${cx + dir*k*Ru},${p2.y} ${p2.x},${p2.y}`;
      }
    }
    return d;
  }

  state(item: any): 'locked' | 'unlocked' | 'current' | 'completed' {
    if (!this.isItemUnlocked(item)) return 'locked';
    if (this.isItemAcquired(item)) return 'completed';
    if (item.position !== null && item.position === this.current) return 'current';
    return 'unlocked';
  }

  // Examen de fin de palier CEFRL — uniquement en mode sections :
  // en mode exam (plateforme type TOEIC), les levels « examen blanc » portent aussi isExam
  isCefrlExam(item: any): boolean {
    return !!item.isExam && this.mode === 'sections';
  }

  // Sections examen de fin de palier CEFRL (isExam) — états dédiés basés sur exam.status
  examState(item: any): 'locked' | 'todo' | 'passed' | 'failed' {
    if (!this.isItemUnlocked(item)) return 'locked';
    if (item.exam?.status === 'passed') return 'passed';
    if (item.exam?.status === 'failed') return 'failed';
    return 'todo';
  }

  showCefrlLabel(section: any, index: number): boolean {
    if (!section.cefrl) return false;
    if (index === 0) return true;
    return section.cefrl !== this.sorted[index - 1]?.cefrl;
  }

  lockPath(cx: number, cy: number): string {
    return `M ${cx - 5},${cy - 2} L ${cx - 5},${cy - 9} A 5,5 0 0 1 ${cx + 5},${cy - 9} L ${cx + 5},${cy - 2}`;
  }

  playPath(pos: NodePos): string {
    const x = pos.x - 2, y = pos.y, h = this.R * 0.52;
    return `M ${x - h * 0.55},${y - h} L ${x + h},${y} L ${x - h * 0.55},${y + h} Z`;
  }

  checkPath(pos: NodePos, r: number = this.R): string {
    const cx = pos.x + r - 4, cy = pos.y - r + 4;
    return `M ${cx - 4},${cy} l3,3 5,-6`;
  }

  crossPath(pos: NodePos, r: number = this.R): string {
    const cx = pos.x + r - 4, cy = pos.y - r + 4;
    return `M ${cx - 3},${cy - 3} l6,6 M ${cx + 3},${cy - 3} l-6,-6`;
  }

  // ── Nœud examen de fin de palier : carré plus grand + coiffe de diplômé ──

  // Demi-côté du carré examen (légèrement plus grand que les nœuds de cours)
  get RE(): number {
    return this.R + 8;
  }

  // Plateau du mortier (losange aplati)
  examCapBoardPath(pos: NodePos): string {
    const cx = pos.x, cy = pos.y - this.RE * 0.28;
    const w = this.RE * 0.62, h = this.RE * 0.30;
    return `M ${cx},${cy - h} L ${cx + w},${cy} L ${cx},${cy + h} L ${cx - w},${cy} Z`;
  }

  // Calotte sous le plateau (dessinée avant, le plateau la recouvre)
  examCapBasePath(pos: NodePos): string {
    const cx = pos.x, cy = pos.y - this.RE * 0.28;
    const bw = this.RE * 0.30, top = cy, bot = cy + this.RE * 0.38;
    return `M ${cx - bw},${top} L ${cx - bw},${bot} Q ${cx},${bot + this.RE * 0.14} ${cx + bw},${bot} L ${cx + bw},${top} Z`;
  }

  // Pompon qui pend du coin droit du plateau
  examCapTasselPath(pos: NodePos): string {
    const cx = pos.x, cy = pos.y - this.RE * 0.28;
    const w = this.RE * 0.62;
    return `M ${cx + w},${cy} L ${cx + w},${cy + this.RE * 0.40}`;
  }

  examCapTasselEnd(pos: NodePos): NodePos {
    const cy = pos.y - this.RE * 0.28;
    return { x: pos.x + this.RE * 0.62, y: cy + this.RE * 0.46 };
  }

  // Anse du cadenas miniature (badge de coin des nœuds examen verrouillés)
  examLockBadgeShacklePath(pos: NodePos): string {
    const cx = pos.x + this.RE - 4, cy = pos.y - this.RE + 4;
    return `M ${cx - 2.5},${cy - 0.5} L ${cx - 2.5},${cy - 3} A 2.5,2.5 0 0 1 ${cx + 2.5},${cy - 3} L ${cx + 2.5},${cy - 0.5}`;
  }

  shortName(section: any): string {
    const name: string = section.name || '';
    const max = this.isMobileLayout ? 12 : 16;
    return name.length > max ? name.substring(0, max - 2) + '…' : name;
  }

  async navigate(item: any): Promise<void> {
    if (!this.isItemUnlocked(item)) {
      if (this.mode === 'levels') this.modal.open(LevelLockedModalComponent, { centered: true });
      return;
    }
    // Examen de fin de palier : pas de levels, on va sur l'écran d'accueil de l'examen
    if (this.isCefrlExam(item)) {
      await this.router.navigate(['/', this.langSrcSrv.current.shortCode, 'cefrl-exam', item.uniqid]);
      return;
    }
    if (this.mode === 'exam') {
      if (item.isBonus) {
        const m = this.modal.open(AlertComponent, { centered: true, windowClass: 'modal-perso' });
        m.componentInstance.type = 'warning';
        m.componentInstance.title = 'Examen blanc';
        m.componentInstance.message = "Les examens blancs sont en cours d'élaboration.";
        return;
      }
      await this.router.navigate(['/', this.langSrcSrv.current.shortCode, 'games', 'exam', item.uniqid]);
    } else if (this.mode === 'levels') {
      await this.router.navigate(['/', this.langSrcSrv.current.shortCode, 'games', item.sectionUniqid, item.uniqid]);
    } else {
      await this.router.navigate(['/', this.langSrcSrv.current.shortCode, 'games', item.uniqid]);
    }
  }

  get flagPath(): string {
    return `assets/images/flags/${this.langTgtSrv.current.flag}`;
  }

  get mapPath(): string {
    return `assets/images/maps/${this.langTgtSrv.current.code}.png`;
  }

  // ── Decorative section assets ──────────────────────
  // Config loaded from assets/images/maps/decor-config.json

  get decorPlacements(): DecorPlacement[] {
    const cfg = this.decorConfigs[this.langTgtSrv.current.code];
    if (!cfg) return [];
    return this.isMobileLayout ? (cfg.mobile ?? cfg.desktop) : cfg.desktop;
  }

  private get decorBaseSize(): number {
    return this.isMobileLayout ? 72 : 104;
  }

  decorPlacementPath(p: DecorPlacement): string {
    return `assets/images/maps/${this.langTgtSrv.current.code}/${p.asset}.png`;
  }

  decorPlacementSize(p: DecorPlacement): number {
    return Math.round(this.decorBaseSize * (p.sizeFactor ?? 1.0));
  }

  decorPlacementTransform(p: DecorPlacement): string {
    if (!p.rotation) return '';
    const pos  = this.nodePositions[p.section - 1];
    if (!pos) return '';
    const size = this.decorPlacementSize(p);
    const reach = this.R + 6;
    const dirScale = this.isMobileLayout ? 0.7 : 1.0;
    const cx = pos.x + p.dir.dx * reach * dirScale;
    const cy = pos.y + p.dir.dy * reach * dirScale;
    return `rotate(${p.rotation} ${cx} ${cy})`;
  }

  decorPlacementPos(p: DecorPlacement): { x: number; y: number } {
    const pos = this.nodePositions[p.section - 1];
    if (!pos) return { x: -9999, y: -9999 };
    const size  = this.decorPlacementSize(p);
    const reach = this.R + 6;
    const dirScale = this.isMobileLayout ? 0.7 : 1.0;
    const cx    = pos.x + p.dir.dx * reach * dirScale;
    const cy    = pos.y + p.dir.dy * reach * dirScale;
    return { x: cx - size / 2, y: cy - size / 2 };
  }

  scrollToCurrent(): void {
    if (!this.svgRef || this.current < 0) return;
    const idx = this.sorted.findIndex(s => s.position === this.current);
    if (idx < 0 || !this.nodePositions[idx]) return;
    const nodeY = this.nodePositions[idx].y;
    setTimeout(() => {
      const svgEl = this.svgRef.nativeElement as HTMLElement;
      const scrollContainer = document.querySelector('.body') as HTMLElement;
      if (!scrollContainer) return;
      const svgRect = svgEl.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const svgTopInContainer = svgRect.top - containerRect.top + scrollContainer.scrollTop;
      const nodePxInSvg = (nodeY / this.SVG_H) * svgRect.height;
      const nodeInContainer = svgTopInContainer + nodePxInSvg;
      scrollContainer.scrollTo({ top: nodeInContainer - scrollContainer.clientHeight / 2, behavior: 'smooth' });
    }, 700);
  }

  trackByIndex(index: number) { return index; }
}
